/**
 * Market registry
 *
 * Single source of truth for market-specific configuration: property types,
 * default currency / area unit, and the extra attributes that matter in that
 * market (EPC rating in the UK, HOA in the US, service charge in the GCC, etc.).
 *
 * Universal-first: any unknown market falls back to `generic`. The form always
 * works with zero market-specific attributes.
 */

export type MarketId =
  | 'us'
  | 'uk'
  | 'eu'
  | 'cy'
  | 'gcc'
  | 'au'
  | 'ca'
  | 'generic';

export type MarketAttributeType = 'select' | 'boolean' | 'text' | 'number';

export interface MarketAttribute {
  key: string;
  label: string;
  type: MarketAttributeType;
  options?: string[]; // for `select`
  placeholder?: string;
  suffix?: string; // displayed after the input (e.g. "/year", "AED")
}

export interface PropertyTypeOption {
  value: string;
  label: string;
  emoji: string;
}

export interface MarketConfig {
  id: MarketId;
  label: string;
  countries: string[];
  currency: string;
  areaUnit: 'sqm' | 'sqft';
  numberFormat: 'EU' | 'US';
  propertyTypes: {
    residential: PropertyTypeOption[];
    commercial: PropertyTypeOption[];
    land: PropertyTypeOption[];
  };
  attributes: MarketAttribute[];
}

const RESIDENTIAL_BASE: PropertyTypeOption[] = [
  { value: 'apartment', label: 'Apartment', emoji: '🏢' },
  { value: 'house', label: 'House', emoji: '🏠' },
  { value: 'villa', label: 'Villa', emoji: '🏡' },
  { value: 'maisonette', label: 'Maisonette', emoji: '🏘️' },
  { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
  { value: 'studio', label: 'Studio', emoji: '🛏️' },
  { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
];

const COMMERCIAL_BASE: PropertyTypeOption[] = [
  { value: 'office', label: 'Office', emoji: '💼' },
  { value: 'shop', label: 'Shop', emoji: '🏪' },
  { value: 'warehouse', label: 'Warehouse', emoji: '📦' },
  { value: 'restaurant', label: 'Restaurant', emoji: '🍽️' },
  { value: 'hotel', label: 'Hotel', emoji: '🏨' },
  { value: 'building', label: 'Building', emoji: '🏗️' },
];

const LAND_BASE: PropertyTypeOption[] = [
  { value: 'residential-plot', label: 'Residential Plot', emoji: '🏡' },
  { value: 'commercial-plot', label: 'Commercial Plot', emoji: '🏗️' },
  { value: 'agricultural', label: 'Agricultural', emoji: '🌾' },
  { value: 'industrial', label: 'Industrial', emoji: '🏭' },
];

const MARKETS: Record<MarketId, MarketConfig> = {
  us: {
    id: 'us',
    label: 'United States',
    countries: ['US'],
    currency: 'USD',
    areaUnit: 'sqft',
    numberFormat: 'US',
    propertyTypes: {
      residential: [
        { value: 'apartment', label: 'Condo', emoji: '🏢' },
        { value: 'house', label: 'Single Family', emoji: '🏠' },
        { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
        { value: 'maisonette', label: 'Duplex', emoji: '🏘️' },
        { value: 'villa', label: 'Luxury Home', emoji: '🏡' },
        { value: 'studio', label: 'Studio', emoji: '🛏️' },
        { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
      ],
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'hoaFee', label: 'HOA fee', type: 'number', suffix: '$/mo' },
      { key: 'propertyTaxYearly', label: 'Property tax', type: 'number', suffix: '$/yr' },
      { key: 'lotSizeAcres', label: 'Lot size', type: 'number', suffix: 'acres' },
      { key: 'mlsNumber', label: 'MLS #', type: 'text' },
      { key: 'schoolDistrict', label: 'School district', type: 'text' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  uk: {
    id: 'uk',
    label: 'United Kingdom',
    countries: ['GB'],
    currency: 'GBP',
    areaUnit: 'sqft',
    numberFormat: 'EU',
    propertyTypes: {
      residential: [
        { value: 'apartment', label: 'Flat', emoji: '🏢' },
        { value: 'house', label: 'House (terraced / semi / detached)', emoji: '🏠' },
        { value: 'maisonette', label: 'Maisonette', emoji: '🏘️' },
        { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
        { value: 'studio', label: 'Studio', emoji: '🛏️' },
        { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
      ],
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'epcRating', label: 'EPC rating', type: 'select', options: ['A', 'B', 'C', 'D', 'E', 'F', 'G'] },
      { key: 'councilTaxBand', label: 'Council tax band', type: 'select', options: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] },
      { key: 'tenure', label: 'Tenure', type: 'select', options: ['Freehold', 'Leasehold', 'Share of freehold'] },
      { key: 'chainFree', label: 'Chain free', type: 'boolean' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  eu: {
    id: 'eu',
    label: 'Europe',
    countries: ['FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'PT', 'IE', 'AT', 'FI', 'GR', 'MT', 'LU', 'SI', 'SK', 'EE', 'LV', 'LT', 'HR', 'PL', 'CZ', 'HU', 'RO', 'BG'],
    currency: 'EUR',
    areaUnit: 'sqm',
    numberFormat: 'EU',
    propertyTypes: {
      residential: RESIDENTIAL_BASE,
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'energyClass', label: 'Energy class', type: 'select', options: ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'] },
      { key: 'heatingType', label: 'Heating', type: 'select', options: ['Gas', 'Electric', 'Heat pump', 'Solar', 'District', 'Oil', 'Wood'] },
      { key: 'floor', label: 'Floor', type: 'text', placeholder: 'e.g. 3rd of 5' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  cy: {
    id: 'cy',
    label: 'Cyprus',
    countries: ['CY'],
    currency: 'EUR',
    areaUnit: 'sqm',
    numberFormat: 'EU',
    propertyTypes: {
      residential: RESIDENTIAL_BASE,
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'energyClass', label: 'Energy class', type: 'select', options: ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'] },
      { key: 'titleDeed', label: 'Title deed', type: 'select', options: ['Available', 'Pending', 'Shared'] },
      { key: 'coveredAreaSqm', label: 'Covered area', type: 'number', suffix: 'm²' },
      { key: 'plotSizeSqm', label: 'Plot size', type: 'number', suffix: 'm²' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  gcc: {
    id: 'gcc',
    label: 'Gulf (UAE, SA, QA, KW, BH, OM)',
    countries: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM'],
    currency: 'AED',
    areaUnit: 'sqft',
    numberFormat: 'EU',
    propertyTypes: {
      residential: [
        { value: 'apartment', label: 'Apartment', emoji: '🏢' },
        { value: 'villa', label: 'Villa', emoji: '🏡' },
        { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
        { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
        { value: 'studio', label: 'Studio', emoji: '🛏️' },
        { value: 'maisonette', label: 'Duplex', emoji: '🏘️' },
        { value: 'house', label: 'Compound Villa', emoji: '🏠' },
      ],
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'serviceCharge', label: 'Service charge', type: 'number', suffix: '/sqft/yr' },
      { key: 'tenure', label: 'Tenure', type: 'select', options: ['Freehold', 'Leasehold'] },
      { key: 'view', label: 'View', type: 'select', options: ['Sea', 'Burj', 'Community', 'Park', 'Pool', 'City', 'Golf'] },
      { key: 'furnishing', label: 'Furnishing', type: 'select', options: ['Fully furnished', 'Semi-furnished', 'Unfurnished'] },
      { key: 'parkingSpaces', label: 'Parking spaces', type: 'number' },
      { key: 'reraPermit', label: 'RERA / DLD permit', type: 'text' },
    ],
  },

  au: {
    id: 'au',
    label: 'Australia / NZ',
    countries: ['AU', 'NZ'],
    currency: 'AUD',
    areaUnit: 'sqm',
    numberFormat: 'US',
    propertyTypes: {
      residential: [
        { value: 'house', label: 'House', emoji: '🏠' },
        { value: 'apartment', label: 'Unit / Apartment', emoji: '🏢' },
        { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
        { value: 'villa', label: 'Villa', emoji: '🏡' },
        { value: 'studio', label: 'Studio', emoji: '🛏️' },
        { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
      ],
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'strataFees', label: 'Strata fees', type: 'number', suffix: '/qtr' },
      { key: 'councilRates', label: 'Council rates', type: 'number', suffix: '/yr' },
      { key: 'landSizeSqm', label: 'Land size', type: 'number', suffix: 'm²' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  ca: {
    id: 'ca',
    label: 'Canada',
    countries: ['CA'],
    currency: 'CAD',
    areaUnit: 'sqft',
    numberFormat: 'US',
    propertyTypes: {
      residential: [
        { value: 'house', label: 'Detached', emoji: '🏠' },
        { value: 'apartment', label: 'Condo', emoji: '🏢' },
        { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
        { value: 'maisonette', label: 'Semi-Detached', emoji: '🏘️' },
        { value: 'studio', label: 'Bachelor', emoji: '🛏️' },
        { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
      ],
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'condoFees', label: 'Condo fees', type: 'number', suffix: '$/mo' },
      { key: 'propertyTaxYearly', label: 'Property tax', type: 'number', suffix: '$/yr' },
      { key: 'mlsNumber', label: 'MLS #', type: 'text' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },

  generic: {
    id: 'generic',
    label: 'Other / Global',
    countries: [],
    currency: 'USD',
    areaUnit: 'sqm',
    numberFormat: 'EU',
    propertyTypes: {
      residential: RESIDENTIAL_BASE,
      commercial: COMMERCIAL_BASE,
      land: LAND_BASE,
    },
    attributes: [
      { key: 'monthlyFees', label: 'Monthly fees', type: 'number' },
      { key: 'yearBuilt', label: 'Year built', type: 'number' },
    ],
  },
};

export const MARKET_OPTIONS = Object.values(MARKETS).map((m) => ({
  value: m.id,
  label: m.label,
}));

export function getMarketConfig(market: string | null | undefined): MarketConfig {
  if (!market) return MARKETS.generic;
  return MARKETS[market as MarketId] ?? MARKETS.generic;
}

/**
 * Detect the most likely market for the current user based on their locale
 * region and IANA timezone. Falls back to `generic`.
 */
export function detectMarketFromLocale(): MarketId {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const locale = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || '';
    let region: string | undefined;
    try {
      region = new Intl.Locale(locale).region?.toUpperCase();
    } catch {
      region = locale.split('-')[1]?.toUpperCase();
    }

    if (region) {
      for (const m of Object.values(MARKETS)) {
        if (m.countries.includes(region)) return m.id;
      }
    }

    // Timezone fallbacks
    if (timezone.startsWith('America/')) {
      if (timezone.includes('Toronto') || timezone.includes('Vancouver') || timezone.includes('Montreal')) return 'ca';
      return 'us';
    }
    if (timezone.startsWith('Europe/London')) return 'uk';
    if (timezone.startsWith('Asia/Nicosia')) return 'cy';
    if (
      timezone.startsWith('Asia/Dubai') ||
      timezone.startsWith('Asia/Riyadh') ||
      timezone.startsWith('Asia/Qatar') ||
      timezone.startsWith('Asia/Kuwait') ||
      timezone.startsWith('Asia/Bahrain') ||
      timezone.startsWith('Asia/Muscat')
    ) {
      return 'gcc';
    }
    if (timezone.startsWith('Europe/')) return 'eu';
    if (timezone.startsWith('Australia/') || timezone.startsWith('Pacific/Auckland')) return 'au';
  } catch {
    // ignore
  }
  return 'generic';
}

/**
 * Infer the market of a scraped listing from its URL (country TLD / known portals)
 * and, as a fallback, the currency detected on the page. Universal: unknown
 * hosts fall back to currency, then `generic`.
 */
export function inferMarketFromListing(
  url: string | null | undefined,
  currency?: string | null,
): MarketId {
  const host = (() => {
    try {
      return new URL(String(url)).hostname.toLowerCase();
    } catch {
      return String(url || '').toLowerCase();
    }
  })();

  const tldMap: Record<string, MarketId> = {
    ae: 'gcc', sa: 'gcc', qa: 'gcc', kw: 'gcc', bh: 'gcc', om: 'gcc',
    uk: 'uk', ca: 'ca', au: 'au', nz: 'au', cy: 'cy',
    de: 'eu', fr: 'eu', es: 'eu', it: 'eu', nl: 'eu', pt: 'eu', gr: 'eu', ie: 'eu', be: 'eu', at: 'eu',
    us: 'us',
  };
  const parts = host.split('.');
  const tld = parts[parts.length - 1];
  const secondLevel = parts.length > 2 ? parts[parts.length - 2] : '';
  if (tld === 'uk' || secondLevel === 'co') {
    if (tld && tldMap[tld]) return tldMap[tld];
  }
  if (tld && tldMap[tld]) return tldMap[tld];
  // No country signal in the host (e.g. any generic .com portal): never assume
  // the US — fall back to the currency detected on the page, then generic.
  if (currency) {
    const byCurrency = Object.values(MARKETS).find(m => m.currency === currency.toUpperCase());
    if (byCurrency) return byCurrency.id;
  }
  return 'generic';
}


export type { MarketConfig as MarketConfigType };
